import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { DefaultPluginUISpec } from 'molstar/lib/mol-plugin-ui/spec';
import { PluginContext } from 'molstar/lib/mol-plugin/context';
import 'molstar/lib/mol-plugin-ui/skin/light.scss';
import '../../styles/molstar-adroit.css';

interface MolstarViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdbContent?: string;
  pdbId?: string;
  uniprotId?: string;
  title?: string;
}

export const MolstarViewer: React.FC<MolstarViewerProps> = ({
  isOpen,
  onClose,
  pdbContent,
  pdbId,
  uniprotId,
  title,
}) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const pluginRef = useRef<PluginContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    // If there's browser history, go back; otherwise navigate to /omics
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/omics');
    }
  };

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    let mounted = true;

    const initViewer = async () => {
      setLoading(true);
      setError(null);

      try {
        // Create plugin
        const plugin = await createPluginUI({
          target: containerRef.current!,
          render: renderReact18,
          spec: {
            ...DefaultPluginUISpec(),
            layout: {
              initial: {
                isExpanded: true,
                showControls: true,
                controlsDisplay: 'reactive',
              },
            },
          },
        });

        if (!mounted) {
          plugin.dispose();
          return;
        }

        pluginRef.current = plugin;

        // Load structure
        if (pdbContent) {
          // Load from PDB string
          const data = await plugin.builders.data.rawData({
            data: pdbContent,
            label: title || 'Structure',
          });
          const trajectory = await plugin.builders.structure.parseTrajectory(data, 'pdb');
          await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');
        } else if (pdbId) {
          // Load from RCSB PDB
          const url = `https://files.rcsb.org/download/${pdbId.toUpperCase()}.cif`;
          const data = await plugin.builders.data.download({ url, isBinary: false });
          const trajectory = await plugin.builders.structure.parseTrajectory(data, 'mmcif');
          await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');
        } else if (uniprotId) {
          // Load from AlphaFold
          const url = `https://alphafold.ebi.ac.uk/files/AF-${uniprotId.toUpperCase()}-F1-model_v4.cif`;
          const data = await plugin.builders.data.download({ url, isBinary: false });
          const trajectory = await plugin.builders.structure.parseTrajectory(data, 'mmcif');
          await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');
        }

        // Default cartoon to secondary-structure colouring so α-helices,
        // β-strands and loops are distinct on first load. Targets only the
        // polymer component, so ball-and-stick CPK on ligands/het/water is
        // left alone. Users can switch theme via the Set Coloring panel.
        const polymerComponents = plugin.managers.structure.hierarchy.current.structures
          .flatMap(s => s.components)
          .filter(c => c.key === 'static-polymer');
        if (polymerComponents.length > 0) {
          await plugin.managers.structure.component.updateRepresentationsTheme(
            polymerComponents,
            { color: 'secondary-structure' }
          );
        }
      } catch (err: any) {
        console.error('Mol* error:', err);
        if (mounted) {
          setError(err.message || 'Failed to load structure');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initViewer();

    return () => {
      mounted = false;
      if (pluginRef.current) {
        pluginRef.current.dispose();
        pluginRef.current = null;
      }
    };
  }, [isOpen, pdbContent, pdbId, uniprotId, title]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
      {/* Floating back button — always visible above Mol* canvas */}
      <button
        onClick={handleBack}
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 14px',
          borderRadius: 9999,
          border: '1.5px solid #0D9488',
          backgroundColor: 'rgba(255,255,255,0.9)',
          color: '#0D9488',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-purple-600">Mol*</span>
              <span className="text-gray-600">Advanced Viewer</span>
            </h2>
            <p className="text-sm text-gray-500">{title || 'Structure'}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              Use controls on left for analysis tools
            </span>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Viewer */}
        <div className="flex-1 relative overflow-hidden min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <span className="text-gray-600">Loading structure in Mol*...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          )}
          <div
            ref={containerRef}
            className="adroit-molstar-wrapper w-full h-full"
            style={{ maxHeight: 'calc(90vh - 100px)', overflow: 'hidden' }}
          />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
          <span>Mol* features: Sequence panel, Binding sites, Ligand highlighting, Measurements, Labels</span>
          <a
            href="https://molstar.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-600 hover:underline"
          >
            molstar.org
          </a>
        </div>
      </div>
    </div>
  );
};

export default MolstarViewer;
